// Generated by tutorials/record.cjs from tutorials/articles/h71-bulk-order.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h71 --text-only` (wording) or
// `node tutorials/record.cjs h71` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'bulk-order',
  catalogId: 'h71',
  title: 'Bulk orders',
  summary: 'Order food in bulk for a hall or a group, by date and meal: make the order from the menu, set how many of each choice the kitchen makes, and change or remove it.',
  audience: 'Dietary managers and kitchen staff at facilities that use production',
  area: 'kitchen',
  learn: [
    'What a bulk order is, and the bulk order residents it is made for',
    'Make a bulk order for a date and one or more meals',
    'Set how many of each choice to make',
    'Change a choice\'s menu, or delete a choice or a whole order',
  ],
  keywords: [
    'bulk order',
    'bulk orders',
    'bulk',
    'bulk order resident',
    'floor stock',
    'family style',
    'hall',
    'quantity',
    'qty',
    'production',
    'production tally',
    'choices',
    'kitchen',
  ],
  steps: [
    {
      id: 'kitchen',
      part: 'Open Bulk Order',
      heading: 'Open the Kitchen pages',
      text: 'Select Kitchen in the menu on the left. The Kitchen page shows its pages as cards: Tray Line Setup, Selective Touch, Bulk Order, Forecasting, Dining Rooms, Employees and Workflows. Which cards you see depends on your role and on what your facility uses.\n\nSelect Bulk Order.',
      media: {
        image: {
          src: '/help/bulk-order/kitchen.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Kitchen page with its cards under Meal Service and Staff & Rooms, the Bulk Order card outlined.',
      },
    },
    {
      id: 'what',
      part: 'Open Bulk Order',
      heading: 'What a bulk order is',
      text: 'Some food goes out in bulk rather than on a tray for one person, for example a hall that is served family style. Your facility sets up a bulk order resident for each one, with its own diet order.\n\nA bulk order is for one date and one meal. When you make it, Dietech Online looks at that day\'s menu and adds a line for each entree choice for each bulk order resident. On each line you type the Qty: how many servings of that choice the kitchen makes. The production reports count them with everything else.',
      textOnly: true,
    },
    {
      id: 'order',
      part: 'Open Bulk Order',
      heading: 'Read an order',
      text: 'Each row is one order: its Date and Mealtime, newest first. Select the arrow at the start of a row to see its choices.\n\nEach line shows the Choice number, the Qty, the bulk order resident\'s Diet Order, the Primary Item for that choice with its Item Type, Size, Consist and Service Item, and, at the far right, the Name of the line. Here Willow Hall has two bulk order residents, Regular and Mechanical Soft, so each has its own line for each choice.',
      media: {
        image: {
          src: '/help/bulk-order/order.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/bulk-order/order.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.5,
        },
        alt: 'The Bulk Order list with today\'s Lunch opened: four lines, Choice 1 Baked Chicken Breast Qty 12 and 4, Choice 2 Beef Stroganoff Qty 8 and 2, for the Regular and Mechanical Soft diet orders.',
      },
    },
    {
      id: 'new',
      part: 'Make a bulk order',
      heading: 'Pick the date and meals',
      text: 'Select New at the top left. The Add New Bulk Order box opens on today, with Breakfast, Lunch and Dinner checked.\n\nPick the Date, and leave checked only the meals you want an order for. Each meal gets an order of its own.',
      media: {
        image: {
          src: '/help/bulk-order/new.webp',
          width: 708,
          height: 510,
        },
        clip: {
          src: '/help/bulk-order/new.mp4',
          width: 708,
          height: 510,
          durationSec: 4.4,
        },
        alt: 'The Add New Bulk Order box with tomorrow\'s date, Lunch and Dinner checked and Breakfast unchecked, and the note that it creates one record per menu choice for each bulk order resident.',
      },
    },
    {
      id: 'ok',
      part: 'Make a bulk order',
      heading: 'Make the order',
      text: 'Select OK. Dietech Online makes the order from that day\'s menu: a line for each choice for each bulk order resident. This can take a moment.\n\nThe new orders are at the top of the list. Open one: every Qty starts at 0.',
      media: {
        image: {
          src: '/help/bulk-order/ok.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/bulk-order/ok.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.6,
        },
        alt: 'The Bulk Order list with tomorrow\'s Lunch and Dinner orders at the top, Lunch opened, its four lines all at Qty 0.',
      },
    },
    {
      id: 'qty',
      part: 'Set the quantities',
      heading: 'Type how many of each choice',
      text: 'Select a line\'s Qty and type how many servings of that choice to make. Press Enter to move down to the next line\'s Qty.\n\nEach Qty is saved as soon as you leave it. There is no Save button.',
      media: {
        image: {
          src: '/help/bulk-order/qty.webp',
          width: 1280,
          height: 226,
        },
        clip: {
          src: '/help/bulk-order/qty.mp4',
          width: 1280,
          height: 226,
          durationSec: 6,
        },
        alt: 'Tomorrow\'s Lunch order with Qty 14 and 4 for Baked Chicken Breast and 6 and 2 for Beef Stroganoff.',
      },
    },
    {
      id: 'qty-only',
      part: 'Set the quantities',
      heading: 'What you can change here',
      text: 'Qty is the only thing you type on this page. The choice, the item and its size, consistency and service item come from the menu and from the bulk order resident\'s diet order.\n\nTo serve something else for a choice, use Modify (below). If the menu for the day changes, delete the order and make it again.',
      textOnly: true,
    },
    {
      id: 'modify',
      part: 'Change or delete an order',
      heading: 'Change what a choice serves',
      text: 'Select a line, then Modify. Selective Menu Entry opens for that choice on the order\'s date and meal: the items checked under Selected are what that line serves.\n\nCheck or uncheck items, change an item or its Size, or add one with New, then select Save. For a date that has passed you can only look, and after five days the menu cannot be opened.',
      media: {
        image: {
          src: '/help/bulk-order/modify.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/bulk-order/modify.mp4',
          width: 1280,
          height: 748,
          durationSec: 2,
        },
        alt: 'The Selective Menu Entry box for WILLOW HALL, MC2 on today\'s lunch: Entree with Beef Stroganoff, Buttered Egg Noodles, Mashed Potatoes and Steamed Broccoli checked, and Dessert with Apple Crisp.',
      },
    },
    {
      id: 'delete',
      part: 'Change or delete an order',
      heading: 'Delete a choice or an order',
      text: 'To remove one line, select it and then Delete. To remove a whole order, select the order\'s own row (its date) and then Delete. Confirm, and the order and every line in it are gone. This cannot be undone.',
      media: {
        image: {
          src: '/help/bulk-order/delete.webp',
          width: 708,
          height: 294,
        },
        alt: 'The Delete box asking: Are you sure you want to delete the selected bulk order?',
      },
    },
  ],
  tips: [
    'Make the order once the menu for that day is final: the choices come from the menu when the order is made.',
    'Type a Qty and press Enter to move straight down to the next choice, so a whole order takes a few seconds.',
    'A choice you will not use can stay at 0, or you can delete just that choice.',
  ],
  notes: [
    'Bulk Order is for facilities that use production. If you do not see it under Kitchen, ask your administrator.',
    'Making and deleting orders needs a Bulk Orders level of 2 or more on your role. Changing a choice\'s menu with Modify needs a Selective Menu level of 2 or more.',
    'A bulk order is made for the facility\'s bulk order residents. A resident becomes one when Type is set to Bulk Order on the resident\'s record. With none, New says there are no bulk order residents.',
    'The choices come from the menu category set as Primary Menu Category in Facility Settings, Menu Cycle. If the day\'s menu has none for a meal, that meal is skipped and Dietech Online says so.',
    'Modify can change a choice only for today or later, and not at all once the order is more than five days old.',
  ],
  related: ['selective-touch', 'running-a-report', 'menu-cycles', 'facility-setup', 'roles-and-access'],
  recorded: '2026-11-03',
};

export default article;
